import React, { useState } from "react";
import "../../App.css";

export type ModalType = "order" | "consult" | null;

interface LeadModalProps {
    type: ModalType;
    onClose: () => void;
}

interface FormData {
    name: string;
    phone: string;
    city: string;
    email: string;
}

interface FormErrors {
    name?: string;
    phone?: string;
    city?: string;
}

const formatPhone = (input: string): string => {
    // оставляем только цифры
    let digits = input.replace(/\D/g, "");

    // если вообще ничего не введено — возвращаем пустую строку,
    // чтобы поле реально можно было очистить
    if (digits.length === 0) {
        return "";
    }

    // убираем ведущую 7 или 8, чтобы всегда приводить к +7
    if (digits.startsWith("7") || digits.startsWith("8")) {
        digits = digits.slice(1);
    }

    // максимум 10 цифр после кода страны
    digits = digits.slice(0, 10);

    let result = "+7";

    if (digits.length > 0) {
        result += " (" + digits.slice(0, 3);
    } else {
        result += " (";
    }

    if (digits.length >= 3) {
        result += ")";
    }

    if (digits.length > 3) {
        result += " " + digits.slice(3, 6);
    }

    if (digits.length > 6) {
        result += "-" + digits.slice(6, 8);
    }

    if (digits.length > 8) {
        result += "-" + digits.slice(8, 10);
    }

    return result;
};

const LeadModal: React.FC<LeadModalProps> = ({ type, onClose }) => {
    const [formData, setFormData] = useState<FormData>({
        name: "",
        phone: "",
        city: "",
        email: "",
    });
    const [errors, setErrors] = useState<FormErrors>({});
    const [submitted, setSubmitted] = useState<boolean>(false);

    if (!type) {
        return null;
    }

    const currentModalText =
        type === "order"
            ? {
                title: "Заказать сайт для фулфилмента",
                subtitle: "Отправьте заявку, и мы вышлем вам подробную информацию.",
            }
            : {
                title: "Получить консультацию",
                subtitle: "С удовольствием ответим на ваши вопросы!",
            };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;

        if (name === "phone") {
            const formatted = formatPhone(value);
            setFormData((prev) => ({ ...prev, phone: formatted }));
            setErrors((prev) => ({ ...prev, phone: undefined }));
            return;
        }

        setFormData((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: undefined }));
    };

    const validateForm = (): FormErrors => {
        const newErrors: FormErrors = {};
        if (!formData.name.trim()) newErrors.name = "Введите имя";

        const phoneDigits = formData.phone.replace(/\D/g, "");
        if (phoneDigits.length < 11) {
            newErrors.phone = "Введите корректный телефон";
        }

        if (!formData.city.trim()) newErrors.city = "Укажите город";
        return newErrors;
    };

    const sendToTelegram = async () => {
        await fetch("https://d5daifm19kvo4uileqj8.aqkd4clz.apigw.yandexcloud.net/lead", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                type: type === "order" ? "Заказ" : "Консультация",
                name: formData.name,
                phone: formData.phone,
                city: formData.city,
                email: formData.email
            })
        });

    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const v = validateForm();
        if (Object.keys(v).length > 0) {
            setErrors(v);
            setSubmitted(false);
            return;
        }

        await sendToTelegram();
        setSubmitted(true);
    };

    const handleBackdropClick = () => {
        onClose();
        setErrors({});
        setSubmitted(false);
        setFormData({
            name: "",
            phone: "",
            city: "",
            email: "",
        });
    };

    const handleModalClick = (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
    };

    return (
        <div className="modal-backdrop" onClick={handleBackdropClick}>
            <div className="modal" onClick={handleModalClick}>
                <button
                    className="modal-close"
                    onClick={handleBackdropClick}
                    type="button"
                >
                    ✕
                </button>
                <h3 className="modal-title">{currentModalText.title}</h3>
                <p className="modal-subtitle">{currentModalText.subtitle}</p>

                <form className="modal-form" onSubmit={handleSubmit}>
                    <div className="form-row">
                        <label>
                            Имя *
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                className={errors.name ? "input-error" : ""}
                                placeholder="Как к вам обращаться?"
                            />
                            {errors.name && (
                                <span className="error-text">{errors.name}</span>
                            )}
                        </label>
                    </div>
                    <div className="form-row">
                        <label>
                            Телефон *
                            <input
                                type="tel"
                                name="phone"
                                value={formData.phone}
                                onChange={handleInputChange}
                                className={errors.phone ? "input-error" : ""}
                                placeholder="+7 (___) ___-__-__"
                            />
                            {errors.phone && (
                                <span className="error-text">{errors.phone}</span>
                            )}
                        </label>
                    </div>
                    <div className="form-row">
                        <label>
                            Город *
                            <input
                                type="text"
                                name="city"
                                value={formData.city}
                                onChange={handleInputChange}
                                className={errors.city ? "input-error" : ""}
                                placeholder="Ваш город"
                            />
                            {errors.city && (
                                <span className="error-text">{errors.city}</span>
                            )}
                        </label>
                    </div>
                    <div className="form-row">
                        <label>
                            E-mail
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                placeholder="По желанию"
                            />
                        </label>
                    </div>

                    <button type="submit" className="modal-submit-btn">
                        Отправить
                    </button>

                    {submitted && (
                        <p className="success-text">
                            Заявка отправлена. Мы свяжемся с вами в ближайшее время.
                        </p>
                    )}
                </form>

                <p className="modal-alt-contact">
                    или напишите{" "}
                    <a
                        href="https://t.me/zayaa_iz_rayaa"
                        target="_blank"
                        rel="noreferrer"
                        className="modal-alt-contact-link"
                    >
                        нам
                    </a>{" "}
                    сами!
                </p>
            </div>
        </div>
    );
};

export default LeadModal;
